import { useState } from "react";

import { cn } from "@/lib/utils";
import { MONTHS_SHORT, getCenturyStart, getDecadeStart } from "@/lib/date";

import type { DrillMode } from "./types";

interface DrillCalendarProps {
  initialYear: number;
  initialMonth: number;
  onSelectDate: (date: Date) => void;
  onClose: () => void;
}

interface GCProps {
  label: string;
  selected?: boolean;
  outOfRange?: boolean;
  isToday?: boolean;
  small?: boolean;
  onClick: () => void;
}

function GridCell({ label, selected, outOfRange, isToday, small, onClick }: GCProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-2xl px-1 py-2.5 text-center transition-colors",
        small ? "text-[11px]" : "text-[13px]",
        selected
          ? "bg-brand font-semibold text-white"
          : outOfRange
            ? "text-muted-foreground hover:bg-brand-light/40 hover:text-brand"
            : "text-foreground hover:bg-brand-light/40 hover:text-brand",
        isToday && !selected && "border-[1.5px] border-brand",
      )}
    >
      {label}
    </button>
  );
}

/** Drill-down nav: month-grid → year-grid → decade-grid. Replaces the popup
 *  when active, sharing the same width. */
export function DrillCalendar({
  initialYear,
  initialMonth,
  onSelectDate,
  onClose,
}: DrillCalendarProps) {
  const [mode, setMode] = useState<DrillMode>("month");
  const [viewYear, setViewYear] = useState(initialYear);
  const today = new Date();

  const nav = (delta: number) => {
    if (mode === "month") setViewYear((p) => p + delta);
    else if (mode === "year") setViewYear((p) => p + delta * 10);
    else setViewYear((p) => p + delta * 100);
  };

  const label =
    mode === "month"
      ? `${viewYear}`
      : mode === "year"
        ? `${getDecadeStart(viewYear)} – ${getDecadeStart(viewYear) + 9}`
        : `${getCenturyStart(viewYear)} – ${getCenturyStart(viewYear) + 99}`;

  const renderMonths = () => (
    <div className="grid grid-cols-4 gap-1.5">
      {MONTHS_SHORT.map((m, i) => (
        <GridCell
          key={i}
          label={m}
          selected={i === initialMonth}
          onClick={() => onSelectDate(new Date(viewYear, i, 1))}
        />
      ))}
    </div>
  );

  const renderYears = () => {
    const ds = getDecadeStart(viewYear);
    const yrs: number[] = [];
    for (let y = ds - 1; y <= ds + 10; y++) yrs.push(y);
    return (
      <div className="grid grid-cols-4 gap-1.5">
        {yrs.map((y) => (
          <GridCell
            key={y}
            label={String(y)}
            selected={y === initialYear}
            outOfRange={y < ds || y > ds + 9}
            isToday={y === today.getFullYear()}
            onClick={() => {
              setViewYear(y);
              setMode("month");
            }}
          />
        ))}
      </div>
    );
  };

  const renderDecades = () => {
    const cs = getCenturyStart(viewYear);
    const decs: number[] = [];
    for (let d = cs - 10; d <= cs + 100; d += 10) decs.push(d);
    return (
      <div className="grid grid-cols-4 gap-1.5">
        {decs.map((d) => (
          <GridCell
            key={d}
            label={`${d}s`}
            small
            selected={getDecadeStart(initialYear) === d}
            outOfRange={d < cs || d >= cs + 100}
            onClick={() => {
              setViewYear(d + 5);
              setMode("year");
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="w-full overflow-hidden rounded-2xl border bg-popover shadow-lg">
      <div className="flex items-center justify-between border-b border-border/60 px-3 pb-2 pt-3">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="rounded-md px-2 py-1 text-[17px] text-brand hover:bg-accent"
        >
          «
        </button>
        <button
          type="button"
          onClick={() => {
            if (mode === "month") setMode("year");
            else if (mode === "year") setMode("decade");
          }}
          disabled={mode === "decade"}
          className="rounded-md px-2.5 py-1 text-sm font-bold text-foreground hover:bg-accent disabled:cursor-default"
        >
          {label}
        </button>
        <button
          type="button"
          onClick={() => nav(1)}
          className="rounded-md px-2 py-1 text-[17px] text-brand hover:bg-accent"
        >
          »
        </button>
      </div>
      <div className="p-3">
        {mode === "month" && renderMonths()}
        {mode === "year" && renderYears()}
        {mode === "decade" && renderDecades()}
      </div>
      <div className="flex justify-end border-t border-border/60 px-3 py-2">
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>
    </div>
  );
}
